import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Linkedin, Twitter, Facebook, Instagram, Youtube } from 'lucide-react';
import { reopenCookieBanner } from './CookieBanner';

export default function Footer({ onContact, onTerms }) {
  const isHome = window.location.pathname === '/';

  const links = [
    { label: 'How It Works', href: isHome ? '#how-it-works' : '/#how-it-works' },
    { label: 'Services', href: isHome ? '#services' : '/#services' },
    { label: 'FAQ', href: isHome ? '#faq' : '/#faq' },
    { label: 'Contact', href: '#', onClick: onContact },
  ];

  const legalLinks = [
    { label: 'Privacy Policy', href: '/privacy-policy' },
    { label: 'Terms of Use', href: '#', onClick: onTerms },
    { label: 'Cookie Settings', href: '#', onClick: reopenCookieBanner },
  ];

  const socialLinks = [
    { icon: Linkedin, href: 'https://www.linkedin.com/company/primeslink-ai/' },
    { icon: Twitter, href: 'https://x.com/PrimeslinkHQ' },
    { icon: Facebook, href: '#' },
    { icon: Instagram, href: '#' },
    { icon: Youtube, href: 'https://youtube.com/@PrimeslinkOfficial' },
  ];

  return (
    <footer id="footer" className="bg-dark-950/90 backdrop-blur-md border-t border-white/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center gap-2 mb-4">
              <img src="/logo.jpg" alt="Primeslink Logo" className="h-8 w-8 rounded-lg object-cover" />
              <span className="text-white font-bold text-lg">Primeslink</span>
            </div>
            <p className="text-gray-400 text-sm">
              Less Work. More Growth. - Your AI Automation Partner
            </p>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h4 className="text-white font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {links.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.onClick ? '#' : link.href}
                    onClick={(e) => {
                      if (link.onClick) {
                        e.preventDefault();
                        link.onClick();
                      }
                    }}
                    className="text-gray-400 hover:text-primary-500 transition-colors text-sm cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Legal */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h4 className="text-white font-semibold mb-4">Legal</h4>
            <ul className="space-y-2">
              {legalLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      if (link.onClick) {
                        e.preventDefault();
                        link.onClick();
                      }
                    }}
                    className="text-gray-400 hover:text-primary-500 transition-colors text-sm cursor-pointer"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h4 className="text-white font-semibold mb-4">Contact</h4>
            <div className="space-y-3">
              <a href="mailto:info@primeslink.com" className="flex items-center gap-2 text-gray-400 hover:text-primary-500 transition-colors text-sm">
                <Mail size={16} />
                info@primeslink.com
              </a>
              <a href="tel:+19145255267" className="flex items-center gap-2 text-gray-400 hover:text-primary-500 transition-colors text-sm">
                <Phone size={16} />
                +1 914 525 5267
              </a>
              <a
                href="https://maps.google.com/?q=620+Mamaroneck+Ave+%2324,+White+Plains,+NY+10605"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 text-gray-400 hover:text-primary-500 transition-colors text-sm"
              >
                <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                <span>620 Mamaroneck Ave #24<br />White Plains, NY 10605-2081</span>
              </a>
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © 2026 Primeslink. All rights reserved.
            </p>

            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                const isLive = social.href !== '#';
                return (
                  <motion.a
                    key={index}
                    href={social.href}
                    target={isLive ? '_blank' : undefined}
                    rel={isLive ? 'noopener noreferrer' : undefined}
                    whileHover={{ scale: 1.2, color: '#0ea5e9' }}
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center text-gray-400 hover:text-primary-500 hover:bg-primary-500/20 transition-all"
                  >
                    <Icon size={18} />
                  </motion.a>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
